/**
 * BuscaDeTelas — "Ir para…" (atalhos `/` e Ctrl+K; só existe com mais de 20 telas visíveis, padrão A item 4).
 *
 * Diálogo modal com um campo (`role="combobox"`) e a lista de resultados (`role="listbox"`), com o padrão
 * de teclado da combobox: setas movem a opção ativa (`aria-activedescendant`), Enter abre, Esc fecha, e o
 * foco volta ao botão de origem. Procura em rótulo, sinônimos e nome do grupo, sem acento (`registro.ts`).
 * Mostra só as telas que o papel pode abrir: quem passa a lista é a casca, já filtrada.
 */
import { Search } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { useDialogoAcessivel } from "../../lib/dialogo-acessivel.js";
import { buscarTelas } from "./registro.js";
import type { GrupoNav, ItemNav } from "./tipos.js";
import { junta } from "./util.js";

export function BuscaDeTelas({
  aberta,
  aoFechar,
  itens,
  grupos,
  aoEscolher,
}: {
  aberta: boolean;
  aoFechar: () => void;
  itens: readonly ItemNav[];
  grupos: readonly GrupoNav[];
  /** Chamado com a tela escolhida; a casca fecha o diálogo e navega. */
  aoEscolher: (item: ItemNav) => void;
}) {
  const id = useId();
  const ref = useDialogoAcessivel<HTMLDivElement>({ aberto: aberta, aoFechar, conterFoco: true, focoInicial: "input" });
  const [termo, setTermo] = useState("");
  const [ativo, setAtivo] = useState(0);
  const resultados = useMemo(() => buscarTelas(itens, grupos, termo, 12), [itens, grupos, termo]);
  if (!aberta) return null;

  const idLista = `${id}-lista`;
  const idOpcao = (i: number) => `${id}-op-${i}`;
  const atual = Math.min(ativo, Math.max(0, resultados.length - 1));

  return (
    <div className="casca-gaveta-raiz casca-busca-raiz">
      <div className="casca-gaveta-fundo" onClick={aoFechar} aria-hidden="true" />
      <div ref={ref} role="dialog" aria-modal="true" aria-label="Ir para uma tela" tabIndex={-1} className="casca-busca-painel">
        <div className="casca-busca-campo">
          <Search size={18} aria-hidden="true" />
          <input
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={idLista}
            aria-activedescendant={resultados.length ? idOpcao(atual) : undefined}
            aria-autocomplete="list"
            aria-label="Nome da tela"
            placeholder="Digite o nome da tela"
            autoComplete="off"
            spellCheck={false}
            value={termo}
            onChange={(e) => { setTermo(e.target.value); setAtivo(0); }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") { e.preventDefault(); setAtivo((a) => Math.min(a + 1, resultados.length - 1)); }
              else if (e.key === "ArrowUp") { e.preventDefault(); setAtivo((a) => Math.max(a - 1, 0)); }
              else if (e.key === "Enter") { e.preventDefault(); const r = resultados[atual]; if (r) aoEscolher(r.item); }
            }}
          />
        </div>
        <ul id={idLista} role="listbox" aria-label="Telas" className="casca-busca-lista">
          {resultados.map((r, i) => {
            const Icone = r.item.icone;
            return (
              <li
                key={r.item.id}
                id={idOpcao(i)}
                role="option"
                aria-selected={i === atual}
                className={junta("casca-busca-opcao", i === atual && "casca-busca-opcao-ativa")}
                onMouseMove={() => setAtivo(i)}
                onClick={() => aoEscolher(r.item)}
              >
                <Icone size={20} strokeWidth={1.75} aria-hidden="true" />
                <span className="casca-busca-opcao-nome">{r.item.rotulo}</span>
                {r.grupo && <span className="casca-busca-opcao-grupo">{r.grupo}</span>}
              </li>
            );
          })}
        </ul>
        <p className="casca-busca-estado" role="status" aria-live="polite">
          {resultados.length === 0 ? `Nenhuma tela encontrada para “${termo.trim()}”.` : `${resultados.length} ${resultados.length === 1 ? "tela" : "telas"}. Use as setas e Enter.`}
        </p>
      </div>
    </div>
  );
}
